import { Badge } from "@/components/ui/badge";
import { useAdminTenants } from "@/features/admin";
import { useBackOfficeAccess } from "@/features/auth/back-office-access";
import { useConnectors } from "@/features/connectors/hooks/use-connectors";
import {
  useDeviceBases,
  useDevices,
} from "@/features/devices/hooks/use-devices";
import { useClientId } from "@/features/utils/api-scope";
import { useDateFnsLocale } from "@/features/utils/i18n";
import { formatDuration } from "@/features/utils/numbers";
import { formatDistanceToNow } from "date-fns";
import { StatCard } from "../../analytics/components/stat-card";

/**
 * What is worth knowing about a client before opening any of the other tabs.
 *
 * Two rows on purpose. The first is *activity* — is this client actually using
 * the product — and comes from the cross-tenant rollup the clients list already
 * reads. The second is *installation*: how many phones, how many of them are on
 * the line right now, how many bases, how many channels. That second row is the
 * one a support call needs, and it was previously three screens away — and the
 * one the read-only account (sales) is not shown: its reads are SYSADMIN-only.
 */
export function ClientOverviewTab() {
  const clientId = useClientId();
  const locale = useDateFnsLocale();
  const access = useBackOfficeAccess();

  const { data: tenants = [], isLoading: tenantsLoading } = useAdminTenants(
    30,
    clientId,
  );
  const tenant = tenants.find((item) => item.id === clientId);

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <h3 className="text-lg font-semibold">Activité (30 jours)</h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Minutes"
            value={formatDuration((tenant?.minutes_30d || 0) * 60)}
            isLoading={tenantsLoading}
          />
          <StatCard
            title="Appels"
            value={tenant?.calls_30d ?? 0}
            isLoading={tenantsLoading}
          />
          <StatCard
            title="Sessions"
            value={tenant?.sessions_30d ?? 0}
            isLoading={tenantsLoading}
          />
          <StatCard
            title="Client depuis"
            value={
              tenant?.created_at
                ? formatDistanceToNow(new Date(tenant.created_at), { locale })
                : "—"
            }
            isLoading={tenantsLoading}
          />
        </div>
      </section>

      {access === "admin" && <ClientInstallation clientId={clientId} />}
    </div>
  );
}

/** Phones, bases, channels and numbers — what a support call needs. */
function ClientInstallation({ clientId }: { clientId: string }) {
  const { data: devices = [], isLoading: devicesLoading } =
    useDevices(clientId);
  const { data: bases = [], isLoading: basesLoading } =
    useDeviceBases(clientId);
  const { data: connectors = [], isLoading: connectorsLoading } =
    useConnectors(clientId);

  const online = devices.filter((device) => device.registered).length;
  const suspended = devices.filter(
    (device) => device.status === "suspended",
  ).length;
  const numbers = connectors.reduce(
    (total, connector) =>
      total + (connector.configuration?.phone_numbers?.length || 0),
    0,
  );
  // A base nobody wrote the codes of is the failure the bases tab exists to
  // prevent, so it is counted here rather than waiting to be noticed there.
  const incomplete = bases.filter(
    (base) => !base.base_pin || !base.web_password,
  ).length;

  return (
    <section className="space-y-3">
      <h3 className="text-lg font-semibold">Installation</h3>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Postes VoIP"
          value={devices.length}
          isLoading={devicesLoading}
          hint={
            devices.length > 0 ? (
              <span className="flex flex-wrap items-center gap-2">
                <span>{online} en ligne</span>
                {suspended > 0 && (
                  <Badge variant="destructive">{suspended} suspendu(s)</Badge>
                )}
              </span>
            ) : (
              "Aucun poste déclaré"
            )
          }
          // Every phone offline is not a detail: it is the whole service for
          // a client who answers on handsets.
          tone={
            devices.length > 0 && online === 0
              ? "bad"
              : online < devices.length
                ? "warn"
                : "default"
          }
        />
        <StatCard
          title="Bases"
          value={bases.length}
          isLoading={basesLoading}
          hint={
            incomplete > 0
              ? `${incomplete} sans code complet`
              : bases.length > 0
                ? "Codes renseignés"
                : "Aucune base déclarée"
          }
          tone={incomplete > 0 ? "warn" : "default"}
        />
        <StatCard
          title="Canaux"
          value={connectors.length}
          isLoading={connectorsLoading}
        />
        <StatCard
          title="Numéros"
          value={numbers}
          isLoading={connectorsLoading}
        />
      </div>
    </section>
  );
}
